"use client";

import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { DataTable } from "@/components/data-table";
import { DeleteDialog } from "@/components/shared/delete-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import {
	categoriesQueryOptions,
	useCategories,
} from "@/features/categories/hooks/use-categories";
import type { Category } from "@/features/categories/types";
import { useBulkDeleteProducts } from "@/features/products/hooks/use-bulk-delete-products";
import { useBulkUpdateProducts } from "@/features/products/hooks/use-bulk-update-products";
import { useDeleteProduct } from "@/features/products/hooks/use-delete-product";
import { useDuplicateProduct } from "@/features/products/hooks/use-duplicate-product";
import {
	productsQueryOptions,
	useProducts,
} from "@/features/products/hooks/use-products";
import { useUpdateProductPosition } from "@/features/products/hooks/use-update-product-position";
import { resolveStoreId } from "@/lib/store-loader";

export const Route = createFileRoute("/$store/_layout/products")({
	loader: async ({ context, params }) => {
		const storeId = await resolveStoreId(context.queryClient, params.store);
		if (!storeId) return;
		await Promise.all([
			context.queryClient.ensureQueryData(
				productsQueryOptions(params.store, storeId),
			),
			context.queryClient.ensureQueryData(
				categoriesQueryOptions(params.store, storeId),
			),
		]);
	},
	component: RouteComponent,
});

type ProductRow = {
	id: string | number;
	image?: string;
	title: string;
	slug: string;
	status: string;
	sortOrder?: number;
};

type CategoryOption = {
	id: string;
	name: string;
};

function RouteComponent() {
	const { products, isLoading, error } = useProducts();
	const { categories = [] } = useCategories();
	const deleteProductMutation = useDeleteProduct();
	const duplicateProductMutation = useDuplicateProduct();
	const bulkDeleteProductsMutation = useBulkDeleteProducts();
	const bulkUpdateProductsMutation = useBulkUpdateProducts();
	const updatePositionMutation = useUpdateProductPosition();

	const [productToDelete, setProductToDelete] =
		React.useState<ProductRow | null>(null);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
	const [duplicatingProductId, setDuplicatingProductId] = React.useState<
		string | number | null
	>(null);

	const openDeleteDialog = React.useCallback((product: ProductRow) => {
		setProductToDelete(product);
		setIsDeleteDialogOpen(true);
	}, []);

	const handleConfirmDelete = React.useCallback(() => {
		if (productToDelete) {
			deleteProductMutation.mutate(productToDelete.id.toString(), {
				onSettled: () => {
					setProductToDelete(null);
					setIsDeleteDialogOpen(false);
				},
			});
		}
	}, [productToDelete, deleteProductMutation]);

	const handleDuplicateProduct = React.useCallback(
		(product: ProductRow) => {
			setDuplicatingProductId(product.id);

			duplicateProductMutation.mutate(product.id, {
				onSettled: () => {
					setDuplicatingProductId((current) =>
						String(current) === String(product.id) ? null : current,
					);
				},
			});
		},
		[duplicateProductMutation],
	);

	const categoryOptions = React.useMemo(() => {
		const flatten = (input: Category[]): CategoryOption[] =>
			input.flatMap((category) => {
				const children = Array.isArray(category.children)
					? category.children
					: [];
				return [{ id: category.id, name: category.name }, ...flatten(children)];
			});

		return flatten(categories);
	}, [categories]);

	const handleBulkUpdate = React.useCallback(
		async (payload: {
			productIds: string[];
			status?: "draft" | "active" | "archived";
			categoryIds?: string[];
			stockQuantity?: number;
		}) => {
			await bulkUpdateProductsMutation.mutateAsync(payload);
		},
		[bulkUpdateProductsMutation],
	);

	const handleBulkDelete = React.useCallback(
		async (payload: { productIds: string[] }) => {
			await bulkDeleteProductsMutation.mutateAsync(payload);
		},
		[bulkDeleteProductsMutation],
	);

	const handleSavePosition = React.useCallback(
		async (productId: string | number, newOrder: number) => {
			await updatePositionMutation.mutateAsync({
				productId: productId.toString(),
				sortOrder: newOrder,
			});
		},
		[updatePositionMutation],
	);

	if (isLoading) {
		return <LoadingSpinner className="h-64" />;
	}

	if (error) {
		return <ErrorState title="Erro ao carregar produtos" error={error} />;
	}

	return (
		<>
			<DataTable
				data={products}
				onRequestDelete={openDeleteDialog}
				onRequestDuplicate={handleDuplicateProduct}
				duplicatingProductId={duplicatingProductId}
				categoryOptions={categoryOptions}
				onBulkUpdate={handleBulkUpdate}
				isBulkUpdating={bulkUpdateProductsMutation.isPending}
				onBulkDelete={handleBulkDelete}
				isBulkDeleting={bulkDeleteProductsMutation.isPending}
				onSavePosition={handleSavePosition}
			/>
			<DeleteDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				title="Excluir produto"
				itemName={productToDelete?.title}
				onConfirmDelete={handleConfirmDelete}
				isPending={deleteProductMutation.isPending}
			/>
		</>
	);
}
